import { motion } from 'framer-motion';
import { useInView } from '../hooks/useInView';

const WindowTypes = () => {
  const [ref, isInView] = useInView({ threshold: 0.1 });

  return (
    <section ref={ref} className="py-16 md:py-20 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16 lg:mb-20"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Nos Expertises en <strong className="text-teal-600">Réparation</strong>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Spécialistes de la réparation de fenêtre à Toulouse et dans toute la Haute-Garonne depuis plus de 15 ans
          </p>
        </motion.div>

        {/* Bloc 1 - Réparation Fenêtres PVC */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 mb-16 md:mb-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative w-full h-[400px] md:h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/c21a14dbe62e737c7190c5bf02ff31ad.jpg"
                alt="Réparation fenêtres PVC Toulouse Haute-Garonne"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-teal-900/60 via-transparent to-transparent"></div>
              
              <div className="absolute bottom-4 md:bottom-6 left-4 md:left-6 bg-white rounded-xl shadow-xl p-3 md:p-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-2 md:mr-3 flex-shrink-0">
                    <i className="ri-window-line text-teal-600 text-xl md:text-2xl"></i>
                  </div>
                  <div>
                    <p className="text-base md:text-lg font-bold text-gray-900">Fenêtres PVC</p>
                    <p className="text-xs md:text-sm text-gray-600">Expert certifié</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
              <strong className="text-teal-600">Réparation de Fenêtre PVC</strong> à Toulouse
            </h3>
            <p className="text-sm md:text-base text-gray-600 mb-3 md:mb-4 leading-relaxed">
              Spécialiste de la <strong>réparation de fenêtre PVC à Toulouse</strong> et dans toute la <strong>Haute-Garonne</strong>, nous intervenons rapidement pour tous vos problèmes de fenêtres PVC : joints défectueux, mécanismes de fermeture cassés, vitrage fissuré ou problèmes d'étanchéité.
            </p>
            <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6 leading-relaxed">
              Nos techniciens qualifiés réalisent le <strong>remplacement de joints de fenêtre PVC</strong>, la <strong>réparation de mécanismes d'ouverture</strong>, le <strong>changement de vitrage</strong> et le <strong>réglage de vos fenêtres PVC</strong> pour une isolation thermique et phonique optimale. Intervention sous 24h à Toulouse, Colomiers, Blagnac et toute la Haute-Garonne.
            </p>

            <div className="space-y-2 md:space-y-3 mb-4 md:mb-6">
              <div className="flex items-center text-gray-700">
                <i className="ri-check-line text-teal-600 mr-2 md:mr-3 text-lg md:text-xl"></i>
                <span className="text-sm md:text-base">Remplacement de joints d'étanchéité PVC</span>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-check-line text-teal-600 mr-2 md:mr-3 text-lg md:text-xl"></i>
                <span className="text-sm md:text-base">Réparation de mécanismes oscillo-battants</span>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-check-line text-teal-600 mr-2 md:mr-3 text-lg md:text-xl"></i>
                <span className="text-sm md:text-base">Changement de vitrage double ou triple</span>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-check-line text-teal-600 mr-2 md:mr-3 text-lg md:text-xl"></i>
                <span className="text-sm md:text-base">Réglage et ajustement de fenêtre PVC</span>
              </div>
            </div>

            <a
              href="#contact"
              className="inline-block bg-gradient-to-r from-teal-500 to-teal-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold text-sm md:text-base hover:from-teal-600 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Devis Gratuit PVC
            </a>
          </motion.div>
        </div>

        {/* Bloc 2 - Réparation Fenêtres Bois (inversé) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 mb-16 md:mb-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="order-2 lg:order-1"
          >
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
              <strong className="text-orange-600">Réparation de Fenêtre Bois</strong> à Toulouse
            </h3>
            <p className="text-sm md:text-base text-gray-600 mb-3 md:mb-4 leading-relaxed">
              Expert en <strong>réparation de fenêtre bois à Toulouse</strong> et en <strong>Haute-Garonne</strong>, nous restaurons et réparons vos fenêtres anciennes et modernes en bois. Notre savoir-faire artisanal permet de redonner vie à vos menuiseries bois tout en préservant leur cachet authentique.
            </p>
            <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6 leading-relaxed">
              Nous réalisons le <strong>traitement du bois contre l'humidité</strong>, la <strong>réparation de bois pourri</strong>, le <strong>ponçage et la peinture de fenêtre bois</strong>, ainsi que le <strong>remplacement de vitrage sur fenêtre ancienne</strong>. Service disponible à Toulouse, Tournefeuille, Muret et dans toute la Haute-Garonne pour la <strong>restauration de fenêtres en bois</strong>.
            </p>

            <div className="space-y-2 md:space-y-3 mb-4 md:mb-6">
              <div className="flex items-center text-gray-700">
                <i className="ri-check-line text-orange-600 mr-2 md:mr-3 text-lg md:text-xl"></i>
                <span className="text-sm md:text-base">Traitement et protection du bois</span>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-check-line text-orange-600 mr-2 md:mr-3 text-lg md:text-xl"></i>
                <span className="text-sm md:text-base">Réparation de bois abîmé ou pourri</span>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-check-line text-orange-600 mr-2 md:mr-3 text-lg md:text-xl"></i>
                <span className="text-sm md:text-base">Ponçage, lasure et peinture</span>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-check-line text-orange-600 mr-2 md:mr-3 text-lg md:text-xl"></i>
                <span className="text-sm md:text-base">Restauration de fenêtre ancienne</span>
              </div>
            </div>

            <a
              href="#contact"
              className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold text-sm md:text-base hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Devis Gratuit Bois
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative order-1 lg:order-2"
          >
            <div className="relative w-full h-[400px] md:h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=elegant%20wooden%20window%20frame%20with%20natural%20wood%20grain%20texture%20traditional%20craftsmanship%20quality%20joinery%20detail%20showing%20hinges%20hardware%20classic%20design%20installed%20in%20charming%20residential%20home%20warm%20tones%20simple%20background%20highlighting%20authentic%20wood%20character%20and%20artisan%20quality&width=800&height=600&seq=wood-window-repair-v2&orientation=landscape"
                alt="Réparation fenêtres bois Toulouse Haute-Garonne"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-orange-900/60 via-transparent to-transparent"></div>
              
              <div className="absolute bottom-4 md:bottom-6 right-4 md:right-6 bg-white rounded-xl shadow-xl p-3 md:p-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-2 md:mr-3 flex-shrink-0">
                    <i className="ri-home-4-line text-orange-600 text-xl md:text-2xl"></i>
                  </div>
                  <div>
                    <p className="text-base md:text-lg font-bold text-gray-900">Fenêtres Bois</p>
                    <p className="text-xs md:text-sm text-gray-600">Artisan qualifié</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bloc 3 - Réparation Fenêtres Aluminium */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 mb-16 md:mb-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="relative"
          >
            <div className="relative w-full h-[400px] md:h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=sleek%20modern%20aluminum%20window%20frame%20with%20slim%20profile%20contemporary%20design%20metallic%20finish%20large%20glass%20panels%20minimalist%20aesthetic%20installed%20in%20modern%20architectural%20building%20clean%20lines%20precision%20engineering%20simple%20neutral%20background%20showcasing%20aluminum%20craftsmanship&width=800&height=600&seq=alu-window-repair-v2&orientation=landscape"
                alt="Réparation fenêtres aluminium Toulouse Haute-Garonne"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-teal-900/60 via-transparent to-transparent"></div>
              
              <div className="absolute bottom-4 md:bottom-6 left-4 md:left-6 bg-white rounded-xl shadow-xl p-3 md:p-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-2 md:mr-3 flex-shrink-0">
                    <i className="ri-building-line text-teal-600 text-xl md:text-2xl"></i>
                  </div>
                  <div>
                    <p className="text-base md:text-lg font-bold text-gray-900">Fenêtres Alu</p>
                    <p className="text-xs md:text-sm text-gray-600">Technicien expert</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
              <strong className="text-teal-600">Réparation de Fenêtre Aluminium</strong> à Toulouse
            </h3>
            <p className="text-sm md:text-base text-gray-600 mb-3 md:mb-4 leading-relaxed">
              Professionnel de la <strong>réparation de fenêtre aluminium à Toulouse</strong> et en <strong>Haute-Garonne</strong>, nous intervenons sur tous types de menuiseries alu : fenêtres coulissantes, oscillo-battantes, à soufflet. Notre expertise couvre la réparation de mécanismes, le remplacement de vitrage et l'entretien de vos fenêtres aluminium.
            </p>
            <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6 leading-relaxed">
              Nous assurons la <strong>réparation de rails de fenêtre coulissante alu</strong>, le <strong>remplacement de joints aluminium</strong>, la <strong>réparation de crémones et poignées</strong>, ainsi que le <strong>changement de vitrage sur châssis alu</strong>. Intervention rapide à Toulouse, Balma, Ramonville et dans toute la Haute-Garonne.
            </p>

            <div className="space-y-2 md:space-y-3 mb-4 md:mb-6">
              <div className="flex items-center text-gray-700">
                <i className="ri-check-line text-teal-600 mr-2 md:mr-3 text-lg md:text-xl"></i>
                <span className="text-sm md:text-base">Réparation de rails coulissants aluminium</span>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-check-line text-teal-600 mr-2 md:mr-3 text-lg md:text-xl"></i>
                <span className="text-sm md:text-base">Remplacement de joints et mécanismes</span>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-check-line text-teal-600 mr-2 md:mr-3 text-lg md:text-xl"></i>
                <span className="text-sm md:text-base">Changement de vitrage sur châssis alu</span>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-check-line text-teal-600 mr-2 md:mr-3 text-lg md:text-xl"></i>
                <span className="text-sm md:text-base">Réglage et entretien fenêtre alu</span>
              </div>
            </div>

            <a
              href="#contact"
              className="inline-block bg-gradient-to-r from-teal-500 to-teal-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold text-sm md:text-base hover:from-teal-600 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Devis Gratuit Aluminium
            </a>
          </motion.div>
        </div>

        {/* Bloc 4 - Réparation Baies Vitrées (inversé) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 mb-16 md:mb-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="order-2 lg:order-1"
          >
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
              <strong className="text-orange-600">Réparation de Baie Vitrée</strong> à Toulouse
            </h3>
            <p className="text-sm md:text-base text-gray-600 mb-3 md:mb-4 leading-relaxed">
              Spécialiste de la <strong>réparation de baie vitrée à Toulouse</strong> et dans toute la <strong>Haute-Garonne</strong>, nous intervenons sur tous types de baies vitrées : coulissantes, à galandage, fixes ou pliantes. Notre expertise garantit une réparation durable et une isolation optimale.
            </p>
            <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6 leading-relaxed">
              Nous réalisons la <strong>réparation de rails de baie vitrée coulissante</strong>, le <strong>remplacement de vitrage de grande dimension</strong>, la <strong>réparation de mécanismes de fermeture</strong> et le <strong>changement de joints d'étanchéité</strong>. Service disponible à Toulouse, Cugnaux et dans toute la Haute-Garonne pour vos <strong>baies vitrées</strong>.
            </p>

            <div className="space-y-2 md:space-y-3 mb-4 md:mb-6">
              <div className="flex items-center text-gray-700">
                <i className="ri-check-line text-orange-600 mr-2 md:mr-3 text-lg md:text-xl"></i>
                <span className="text-sm md:text-base">Réparation de rails et roulettes</span>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-check-line text-orange-600 mr-2 md:mr-3 text-lg md:text-xl"></i>
                <span className="text-sm md:text-base">Remplacement de vitrage grand format</span>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-check-line text-orange-600 mr-2 md:mr-3 text-lg md:text-xl"></i>
                <span className="text-sm md:text-base">Réparation de mécanismes coulissants</span>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-check-line text-orange-600 mr-2 md:mr-3 text-lg md:text-xl"></i>
                <span className="text-sm md:text-base">Changement de joints d'isolation</span>
              </div>
            </div>

            <a
              href="#contact"
              className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold text-sm md:text-base hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Devis Gratuit Baies Vitrées
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="relative order-1 lg:order-2"
          >
            <div className="relative w-full h-[400px] md:h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=expansive%20sliding%20glass%20door%20bay%20window%20system%20with%20smooth%20rail%20mechanism%20floor%20to%20ceiling%20glazing%20modern%20aluminum%20frame%20large%20transparent%20panels%20installed%20in%20bright%20contemporary%20living%20room%20simple%20clean%20background%20showcasing%20panoramic%20view%20and%20expert%20installation%20quality&width=800&height=600&seq=bay-window-repair-v2&orientation=landscape"
                alt="Réparation baies vitrées Toulouse Haute-Garonne"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-orange-900/60 via-transparent to-transparent"></div>
              
              <div className="absolute bottom-4 md:bottom-6 right-4 md:right-6 bg-white rounded-xl shadow-xl p-3 md:p-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-2 md:mr-3 flex-shrink-0">
                    <i className="ri-layout-grid-line text-orange-600 text-xl md:text-2xl"></i>
                  </div>
                  <div>
                    <p className="text-base md:text-lg font-bold text-gray-900">Baies Vitrées</p>
                    <p className="text-xs md:text-sm text-gray-600">Expert vitrage</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bloc 5 - Réparation Volets Roulants */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="relative"
          >
            <div className="relative w-full h-[400px] md:h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20electric%20roller%20shutter%20system%20with%20smooth%20aluminum%20slats%20motorized%20mechanism%20remote%20control%20installed%20on%20contemporary%20residential%20window%20showing%20quality%20engineering%20precision%20installation%20simple%20clean%20background%20highlighting%20shutter%20technology%20and%20craftsmanship&width=800&height=600&seq=roller-shutter-repair-v2&orientation=landscape"
                alt="Réparation volets roulants Toulouse Haute-Garonne"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-teal-900/60 via-transparent to-transparent"></div>
              
              <div className="absolute bottom-4 md:bottom-6 left-4 md:left-6 bg-white rounded-xl shadow-xl p-3 md:p-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-2 md:mr-3 flex-shrink-0">
                    <i className="ri-door-line text-teal-600 text-xl md:text-2xl"></i>
                  </div>
                  <div>
                    <p className="text-base md:text-lg font-bold text-gray-900">Volets Roulants</p>
                    <p className="text-xs md:text-sm text-gray-600">Dépannage rapide</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
              <strong className="text-teal-600">Réparation de Volet Roulant</strong> à Toulouse
            </h3>
            <p className="text-sm md:text-base text-gray-600 mb-3 md:mb-4 leading-relaxed">
              Expert en <strong>réparation de volet roulant à Toulouse</strong> et en <strong>Haute-Garonne</strong>, nous dépannons tous types de volets roulants : électriques, manuels, solaires. Notre service couvre le remplacement de lames, la réparation de moteurs et la motorisation de volets existants.
            </p>
            <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6 leading-relaxed">
              Nous assurons le <strong>dépannage de volet roulant électrique</strong>, le <strong>remplacement de lames cassées</strong>, la <strong>réparation de moteurs et télécommandes</strong>, ainsi que la <strong>motorisation de volets manuels</strong>. Intervention d'urgence 24h/24 à Toulouse et dans toute la Haute-Garonne pour vos <strong>volets roulants bloqués</strong>.
            </p>

            <div className="space-y-2 md:space-y-3 mb-4 md:mb-6">
              <div className="flex items-center text-gray-700">
                <i className="ri-check-line text-teal-600 mr-2 md:mr-3 text-lg md:text-xl"></i>
                <span className="text-sm md:text-base">Dépannage moteurs électriques et solaires</span>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-check-line text-teal-600 mr-2 md:mr-3 text-lg md:text-xl"></i>
                <span className="text-sm md:text-base">Remplacement de lames et tablier</span>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-check-line text-teal-600 mr-2 md:mr-3 text-lg md:text-xl"></i>
                <span className="text-sm md:text-base">Réparation de manivelles et sangles</span>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-check-line text-teal-600 mr-2 md:mr-3 text-lg md:text-xl"></i>
                <span className="text-sm md:text-base">Motorisation et automatisation</span>
              </div>
            </div>

            <a
              href="#contact"
              className="inline-block bg-gradient-to-r from-teal-500 to-teal-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold text-sm md:text-base hover:from-teal-600 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Dépannage Urgent 24h/24
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WindowTypes;
